import { Link } from "react-router-dom";
import {
  Boxes,
  Calculator,
  Camera,
  FileText,
  History,
  ImageOff,
  ListChecks,
  MapPin,
  Package,
  Plus,
  Wrench,
  ScanLine,
  Container as ContainerIcon,
} from "lucide-react";

import { useDashboard } from "@/api/queries";
import type { DashboardSummary } from "@/api/types";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { EmptyState, ErrorState, Skeleton } from "@/components/States";
import { PageHeader } from "@/layout/PageHeader";
import { AskHdVault } from "@/features/assistant/AskHdVault";
import { ProcurementSection } from "@/features/dashboard/ProcurementSection";
import { Page } from "@/layout/Page";
import { useAuth, useCanWrite } from "@/auth/useAuth";
import { formatInteger, formatQuantity, formatRelative } from "@/lib/format";

/**
 * The dashboard (TICKET FE-004).
 *
 * Useful in ten seconds. "Demander à HD-Vault" is prominent but does not hide
 * the standard functions, and no counter is invented: a module that does not
 * exist yet says so rather than showing a reassuring zero.
 */
export function DashboardPage() {
  const { user } = useAuth();
  const canWrite = useCanWrite();
  const { data, isLoading, error, refetch } = useDashboard();

  const firstName = user?.display_name?.split(/\s+/)[0] ?? "";

  return (
    <Page>
      <PageHeader
        title={firstName ? `Bonjour ${firstName}` : "Dashboard"}
        subtitle="L'inventaire qui travaille avec toi."
      />

      <AskHdVault />

      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {/* A wrapping flex, not a grid.
          A grid of N columns leaves N - (count mod N) empty cells on its last
          row, and there are seven of these: seven divides neither two nor four,
          so below 1024 px of container there was always exactly one hole — a
          black rectangle a quarter of the row wide, sitting at the right. It
          only closed when the window was maximised and the grid reached seven
          columns, which is why the application looked like it needed a
          full-screen window to lay itself out properly.
          With flex-wrap the last row's items grow into whatever is left, so
          every row is full at every width. */}
      <div className="mt-4 flex shrink-0 flex-wrap gap-3">
        <Kpi
          label="Unités en stock"
          value={data ? formatQuantity(data.total_units_in_stock) : null}
          hint={data ? `${formatInteger(data.stock_position_count)} emplacements occupés` : undefined}
          icon={Boxes}
          loading={isLoading}
          to="/inventory"
        />
        <Kpi
          label="Produits au catalogue"
          value={data ? formatInteger(data.product_count) : null}
          hint={data ? `${formatInteger(data.active_product_count)} actifs` : undefined}
          icon={Package}
          loading={isLoading}
          to="/products"
        />
        <Kpi
          label="Unités réservées"
          value={data ? formatQuantity(data.reserved_units) : null}
          hint="Présent ≠ disponible"
          icon={Boxes}
          loading={isLoading}
          to="/inventory"
        />
        <Kpi
          label="Emplacements"
          value={data ? formatInteger(data.location_count) : null}
          hint={data ? `${formatInteger(data.container_count)} contenants` : undefined}
          icon={MapPin}
          loading={isLoading}
          to="/locations"
        />
        <Kpi
          label="Mouvements (7 j)"
          value={data ? formatInteger(data.movements_last_7_days) : null}
          hint="Réceptions, sorties, transferts"
          icon={History}
          loading={isLoading}
          to="/history"
        />
        <Kpi
          label="Outils / Assets"
          value={data ? formatInteger(data.asset_count) : null}
          hint={
            data
              ? `${formatInteger(data.assets_available)} disponibles · ${formatInteger(data.assets_out_of_service)} indisponibles`
              : undefined
          }
          icon={Wrench}
          loading={isLoading}
          to="/assets"
        />
        <Kpi
          label="Produits sans photo"
          value={data ? formatInteger(data.products_without_photo) : null}
          hint="Une photo évite un aller-retour"
          icon={ImageOff}
          loading={isLoading}
          tone={data && data.products_without_photo > 0 ? "warning" : "neutral"}
          to="/products"
        />
      </div>

      <div className="mt-4 grid gap-3 @4xl:grid-cols-3">
        <Card className="flex flex-col overflow-hidden @4xl:col-span-2">
          <CardHeader
            className="shrink-0"
            title="Activité récente"
            action={
              <Link to="/history" className="text-[12px] text-ink-muted hover:text-ink">
                Voir tout
              </Link>
            }
          />
          <div className="min-w-0">
            <ActivityFeed data={data} loading={isLoading} />
          </div>
        </Card>

        <div className="flex min-w-0 flex-col gap-3">
          <Card>
            <CardHeader title="Actions rapides" />
            {/* Seven actions in a two-column grid left one empty cell on the
                last row, up to 364 px of nothing at 2560. Same wrapping flex as
                the KPI row: the last item grows into what is left. */}
            <CardBody className="flex flex-wrap gap-2">
              {/* Créer se fait sur la page du module, avec son formulaire. Ces
                  raccourcis y ouvrent directement la boîte de dialogue plutôt
                  que d'en faire une seconde ici, qui divergerait le jour où un
                  champ change. Masqués pour un rôle en lecture seule : montrer
                  une action que le serveur refuserait n'aide personne. */}
              {canWrite && (
                <>
                  <QuickAction to="/lists?new=1" icon={ListChecks} label="Nouvelle liste" />
                  <QuickAction
                    to="/estimates?new=1"
                    icon={Calculator}
                    label="Nouvelle estimation"
                  />
                  <QuickAction to="/rfqs?new=1" icon={FileText} label="Nouvelle RFQ" />
                  <QuickAction to="/inventory/add" icon={Plus} label="Ajouter un article" />
                </>
              )}
              <QuickAction to="/inventory" icon={Boxes} label="Voir l'inventaire" />
              <QuickAction to="/locations" icon={MapPin} label="Emplacements" />
              <QuickAction to="/containers" icon={ContainerIcon} label="Contenants" />
              <QuickAction to="/assets" icon={Wrench} label="Outils" />
              <QuickAction to="/history" icon={History} label="Historique" />
              <QuickAction to="/blitz" icon={ScanLine} label="Mode Blitz" />
            </CardBody>
          </Card>

        </div>
      </div>

      {/* Sept modules d'approvisionnement livrés en V1.6-01 à V1.6-05 qui
          n'apparaissaient nulle part ici. Ils vivent sous l'inventaire, pas
          au-dessus : l'écran d'accueil répond d'abord à « qu'est-ce qu'on a ». */}
      <ProcurementSection />
    </Page>
  );
}

function Kpi({
  label,
  value,
  hint,
  icon: Icon,
  loading,
  to,
  tone = "neutral",
}: {
  label: string;
  value: string | null;
  hint?: string;
  icon: typeof Boxes;
  loading?: boolean;
  to: string;
  tone?: "neutral" | "warning";
}) {
  return (
    <Link
      to={to}
      // basis is the *preferred* width; flex-1 lets the row's last items grow
      // to close the gap. min-w-0 keeps a long value from pushing the row wider
      // than the container.
      className="flex min-w-0 flex-1 basis-[calc(50%-0.375rem)] flex-col gap-2 rounded-lg border border-border bg-surface p-3 transition-colors hover:border-border-strong hover:bg-surface-hover @2xl:basis-[190px]"
    >
      <div className="flex items-center gap-2">
        <span
          className={
            tone === "warning"
              ? "flex size-8 items-center justify-center rounded-md bg-warning-subtle text-warning"
              : "flex size-8 items-center justify-center rounded-md bg-surface-raised text-ink-muted"
          }
        >
          <Icon className="size-4" aria-hidden />
        </span>
        <span className="text-[12px] text-ink-muted">{label}</span>
      </div>
      {loading || value === null ? (
        <Skeleton className="h-7 w-20" />
      ) : (
        <span className="tnum text-2xl font-semibold leading-none text-ink">{value}</span>
      )}
      {hint && <span className="text-[11px] text-ink-subtle">{hint}</span>}
    </Link>
  );
}

function ActivityFeed({ data, loading }: { data?: DashboardSummary; loading: boolean }) {
  if (loading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="flex items-center gap-3">
            <Skeleton className="size-8 rounded-md" />
            <Skeleton className="h-4 flex-1" />
          </div>
        ))}
      </div>
    );
  }

  if (!data || data.recent_activity.length === 0) {
    return (
      <EmptyState
        icon={<Camera className="size-6" aria-hidden />}
        title="Aucune activité pour le moment"
        description="Chaque création, déplacement et mouvement de stock apparaîtra ici."
      />
    );
  }

  return (
    <ul className="divide-y divide-border/60">
      {data.recent_activity.map((entry, index) => (
        <li key={`${entry.occurred_at}-${index}`} className="flex items-start gap-3 px-4 py-2.5">
          <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-surface-raised text-ink-subtle">
            <History className="size-4" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] text-ink">{entry.summary}</p>
            <p className="text-[11px] text-ink-subtle">
              {formatRelative(entry.occurred_at)} · {entry.actor_label}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function QuickAction({
  to,
  icon: Icon,
  label,
  comingSoon,
}: {
  to: string;
  icon: typeof Boxes;
  label: string;
  comingSoon?: boolean;
}) {
  return (
    <Link
      to={to}
      className="flex min-w-0 flex-1 basis-[calc(50%-0.25rem)] flex-col items-center gap-1.5 rounded-md border border-border bg-surface-raised px-2 py-3 text-center transition-colors hover:border-border-strong hover:bg-surface-hover"
    >
      <Icon className={comingSoon ? "size-5 text-ink-subtle" : "size-5 text-brand"} aria-hidden />
      <span className={comingSoon ? "text-[11px] text-ink-subtle" : "text-[11px] text-ink"}>
        {label}
      </span>
      {comingSoon && <span className="text-[9px] uppercase text-ink-subtle">à venir</span>}
    </Link>
  );
}
